import { Component } from "@theme/component";
import { morphSection } from "@theme/section-renderer";
import { DiscountUpdateEvent } from "@theme/events";
import { fetchConfig } from "@theme/utilities";
import { cartPerformance } from "@theme/performance";

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 * @property {HTMLElement} [cartDiscountErrorLogin] - The sign-in-required error element.
 * @property {HTMLElement} [cartDiscountStatus] - The promo-code-activated status element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = [
    "cartDiscountError",
    "cartDiscountErrorDiscountCode",
    "cartDiscountErrorShipping",
  ];

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {string | null} */
  #lastSubmittedCode = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(
      "promo:gift-codes-activated",
      this.#onPromoGiftCodesActivated,
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(
      "promo:gift-codes-activated",
      this.#onPromoGiftCodesActivated,
    );
  }

  /**
   * The promo block's evaluator confirms — one evaluate round-trip after the
   * code lands on the cart — that an entered code just earned a gift (the
   * picker is showing because of it). By then the heuristics in
   * `applyDiscount` have already guessed wrong for such codes: "cannot be
   * applied" for a gift code whose gift isn't in the cart yet, or the
   * shipping notice for an applied code with no visible pill. Swap the guess
   * for the choose-your-gift message (SHOP-1418).
   * @param {Event} event
   */
  #onPromoGiftCodesActivated = (event) => {
    if (!(event instanceof CustomEvent)) return;
    const codes = event.detail?.codes;
    if (!Array.isArray(codes) || !this.#lastSubmittedCode) return;
    if (!codes.includes(this.#lastSubmittedCode)) return;

    const { cartDiscountError, cartDiscountStatus } = this.refs;
    if (!cartDiscountStatus) return;
    cartDiscountError.classList.add("hidden");
    for (const message of cartDiscountError.querySelectorAll(
      ".cart-discount__error-text",
    )) {
      message.classList.add("hidden");
    }
    cartDiscountStatus.classList.remove("hidden");
  };

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const {
      cartDiscountError,
      cartDiscountErrorDiscountCode,
      cartDiscountErrorShipping,
    } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (
      !(discountCode instanceof HTMLInputElement) ||
      typeof this.dataset.sectionId !== "string"
    )
      return;

    const discountCodeValue = discountCode.value;
    this.#lastSubmittedCode = discountCodeValue.toUpperCase();

    const abortController = this.#createAbortController();

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) return;

      cartDiscountError.classList.add("hidden");
      cartDiscountErrorDiscountCode.classList.add("hidden");
      cartDiscountErrorShipping.classList.add("hidden");
      this.refs.cartDiscountErrorLogin?.classList.add("hidden");
      this.refs.cartDiscountStatus?.classList.add("hidden");

      // Working state: spinner on the submit button + disabled input until the
      // cart responds. Cleared in `finally` so it lifts on success, error, and
      // abort alike (on the success path the section morph also re-renders a
      // fresh, enabled form).
      this.#setLoading(true);

      const config = fetchConfig("json", {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(","),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      // Shopify refuses to attach some codes with no error surface in the
      // cart API — the accepted response simply comes back without the code.
      // For a signed-out shopper the dominant cause is a code that requires
      // a customer (promo-engine's login gate does this), so prompt sign-in;
      // for a signed-in shopper the cause is something else (eligibility,
      // usage caps), so fall back to the generic message rather than assert
      // a wrong one (SHOP-1418).
      if (
        response.ok &&
        !(data.discount_codes ?? []).find(
          (/** @type {{ code: string; }} */ discount) =>
            discount.code === discountCodeValue,
        )
      ) {
        discountCode.value = "";
        this.#handleDiscountError(
          window.__promoCustomerId ? "discount_code" : "login",
        );
        return;
      }

      if (
        data.discount_codes.find(
          (/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return (
              discount.code === discountCodeValue &&
              discount.applicable === false
            );
          },
        )
      ) {
        discountCode.value = "";
        this.#handleDiscountError("discount_code");
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, "text/html");
      const section = parsedHtml.getElementById(
        `shopify-section-${this.dataset.sectionId}`,
      );
      const discountCodes =
        section?.querySelectorAll(".cart-discount__pill") || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) =>
            element instanceof HTMLLIElement
              ? element.dataset.discountCode
              : null,
          )
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) =>
            existingDiscounts.includes(code),
          ) &&
          data.discount_codes.find(
            (
              /** @type {{ code: string; applicable: boolean; }} */ discount,
            ) => {
              return (
                discount.code === discountCodeValue &&
                discount.applicable === true
              );
            },
          )
        ) {
          this.#handleDiscountError("shipping");
          discountCode.value = "";
          return;
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, newHtml);
    } catch {
      // Fetch may be aborted intentionally via AbortController
    } finally {
      this.#setLoading(false);
      this.#activeFetch = null;
      cartPerformance.measureFromEvent("discount-update:user-action", event);
    }
  };

  /**
   * Toggles the apply-in-flight working state: a spinner replaces the arrow on
   * the submit button and the input is disabled so the code can't be edited or
   * re-submitted until the cart responds.
   * @param {boolean} loading
   */
  #setLoading(loading) {
    this.classList.toggle("is-loading", loading);

    const input = this.querySelector(".cart-discount__input");
    if (input instanceof HTMLInputElement) input.disabled = loading;

    const button = this.querySelector(".cart-discount__button");
    if (button instanceof HTMLButtonElement) {
      button.disabled = loading;
      button.setAttribute("aria-busy", loading ? "true" : "false");
    }
  }

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== "Enter") ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== "string"
    ) {
      return;
    }

    const pill = event.target.closest(".cart-discount__pill");
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    try {
      const config = fetchConfig("json", {
        body: JSON.stringify({
          discount: existingDiscounts.join(","),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(
        this.dataset.sectionId,
        data.sections[this.dataset.sectionId],
      );
    } catch {
      // Fetch may be aborted intentionally via AbortController
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping' | 'login'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const {
      cartDiscountError,
      cartDiscountErrorDiscountCode,
      cartDiscountErrorShipping,
      cartDiscountErrorLogin,
    } = this.refs;
    const target =
      type === "discount_code"
        ? cartDiscountErrorDiscountCode
        : type === "login"
          ? // Falls back to the generic message when the section HTML predates
            // the login span (deploy skew between theme and assets).
            (cartDiscountErrorLogin ?? cartDiscountErrorDiscountCode)
          : cartDiscountErrorShipping;
    cartDiscountError.classList.remove("hidden");
    target.classList.remove("hidden");
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll(".cart-discount__pill");
    for (const pill of discountPills) {
      if (
        pill instanceof HTMLLIElement &&
        typeof pill.dataset.discountCode === "string"
      ) {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get("cart-discount-component")) {
  customElements.define("cart-discount-component", CartDiscount);
}
